/* =========================================================
   Ankit START - DFW Quote Submit Orchestrator
   File: assets/dfw-quote-submit.js

   Purpose
   ---------------------------------------------------------
   Intercepts the quote modal form submission. Calls
   window.dfwXLSX.runQuoteFile() to build the quote Excel,
   converts it to base64, and POSTs to the Cloudflare Worker.

   Phase 3 = uses TEST endpoint (check_config action).
              No real emails sent.
   Phase 4 = changes action to 'send_quote_xlsx' (1-line change).

   Dependencies
   ---------------------------------------------------------
   - assets/dfw-xlsx-export.js   (Phase 1 — provides runQuoteFile)
   - assets/dfw-quote.js         (modal open/close)
   - Cloudflare Worker at WORKER_URL (must be reachable)

   Load order
   ---------------------------------------------------------
   MUST load AFTER dfw-quote.js AND dfw-xlsx-export.js
   ========================================================= */

(function () {
  'use strict';

  /* ---------------------------------------------
     CONFIGURATION
     --------------------------------------------- */
  const WORKER_URL = 'https://excelquote.ankit-ed5.workers.dev';

  /* PHASE 3 = 'check_config' (test mode, no real emails) */
  /* PHASE 4 = 'send_quote_xlsx' (real emails) */
  const WORKER_ACTION = 'send_quote_xlsx';

  const FETCH_TIMEOUT_MS = 20000;  /* 20 seconds */

  const SELECTORS = {
    form:          '[data-dfw-quote-contact-form]',
    overlay:       '[data-dfw-quote-overlay]',
    modal:         '[data-dfw-quote-modal]',
    status:        '[data-dfw-quote-status]',
    submitButton:  '[data-dfw-quote-contact-form] button[type="submit"]',
    closeButton:   '[data-dfw-quote-close]',
    cancelButton:  '[data-dfw-quote-cancel]'
  };

  /* Email regex — basic but reliable */
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---------------------------------------------
     HELPERS
     --------------------------------------------- */
  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function readFormFields(form) {
    function v(name) {
      const el = form.querySelector('[name="' + name + '"]');
      return el ? (el.value || '').trim() : '';
    }
    return {
      hotel_name:   v('contact[Hotel]'),
      contact_name: v('contact[name]'),
      street:       v('contact[Street]'),
      city:         v('contact[City]'),
      state:        v('contact[State]'),
      zip:          v('contact[Zip]'),
      phone:        v('contact[phone]'),
      email:        v('contact[email]'),
      notes:        v('contact[body]'),
      honeypot:     v('contact[honey]')
    };
  }

  function setStatus(message, state) {
    const el = qs(SELECTORS.status);
    if (!el) return;
    el.textContent = message || '';
    el.className = 'dfw-quote-status';
    if (state) el.classList.add('is-show', 'is-' + state);
    if (message) el.classList.add('is-show');
  }

  function clearStatus() {
    const el = qs(SELECTORS.status);
    if (!el) return;
    el.textContent = '';
    el.className = 'dfw-quote-status';
  }

  function setSubmitting(submitting) {
    const btn = qs(SELECTORS.submitButton);
    if (!btn) return;
    if (submitting) {
      btn._origHTML = btn.innerHTML;
      btn.disabled = true;
      btn.style.opacity = '0.65';
      btn.style.cursor = 'not-allowed';
    } else {
      btn.disabled = false;
      btn.style.opacity = '';
      btn.style.cursor = '';
      if (btn._origHTML) btn.innerHTML = btn._origHTML;
    }
  }

  /* ---------------------------------------------
     CONVERT BLOB TO BASE64
     - Uses FileReader, returns base64 string only
       (strips the "data:...;base64," prefix)
     --------------------------------------------- */
  function blobToBase64(blob) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onloadend = function () {
        const result = reader.result || '';
        const base64 = result.split(',')[1] || '';
        if (!base64) {
          reject(new Error('Failed to encode file'));
        } else {
          resolve(base64);
        }
      };
      reader.onerror = function () {
        reject(new Error('FileReader error'));
      };
      reader.readAsDataURL(blob);
    });
  }

  /* ---------------------------------------------
     POST TO WORKER WITH TIMEOUT
     --------------------------------------------- */
  function postToWorker(payload) {
    const controller = new AbortController();
    const timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);

    return fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal
    })
      .then(function (response) {
        clearTimeout(timeoutId);
        return response.text().then(function (text) {
          let data;
          try { data = JSON.parse(text); } catch (e) { data = { ok: false, raw: text }; }
          if (!response.ok) {
            throw new Error('Worker returned ' + response.status + ': ' + (data.error || text));
          }
          return data;
        });
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          throw new Error('Request timed out after ' + (FETCH_TIMEOUT_MS / 1000) + 's');
        }
        throw err;
      });
  }

  /* ---------------------------------------------
     MAIN SUBMIT HANDLER
     - Intercepts form submit
     - Runs the full quote-build-and-send pipeline
     --------------------------------------------- */
  async function handleSubmit(form) {
    /* 1. Read form fields */
    const fields = readFormFields(form);

    /* 2. Validate honeypot */
    if (fields.honeypot) {
      console.warn('[DFW Quote] Honeypot triggered, blocking submission');
      return;  /* silently fail for bots */
    }

    /* 3. Validate email */
    if (!EMAIL_REGEX.test(fields.email)) {
      setStatus('Please enter a valid email address.', 'error');
      const emailField = form.querySelector('[name="contact[email]"]');
      if (emailField) emailField.focus();
      return;
    }

    /* 4. Validate required fields */
    const required = ['hotel_name', 'contact_name', 'street', 'city', 'state', 'zip', 'email', 'notes'];
    for (let i = 0; i < required.length; i++) {
      if (!fields[required[i]]) {
        setStatus('Please fill in all required fields.', 'error');
        return;
      }
    }

    /* 5. Verify dfwXLSX is available */
    /* If Turnstile is on the page but hasn't finished loading a token yet, wait. */
    if (window.turnstile && !window._dfwQuoteToken) {
      setStatus('One moment — finishing verification. Please tap Send again.', 'error');
      setSubmitting(false);
      return;
    }
        if (!window.dfwXLSX || typeof window.dfwXLSX.runQuoteFile !== 'function') {
      setStatus('Quote system not ready. Please refresh and try again.', 'error');
      console.error('[DFW Quote] window.dfwXLSX.runQuoteFile is missing');
      return;
    }

    /* 6. Lock the button, start the pipeline */
    setSubmitting(true);
    setStatus('Preparing your quote...', 'loading');

    let quoteId, fileResult, base64Data;

    try {
      /* 7. Generate quote ID */
      quoteId = window.dfwXLSX.generateQuoteId();

      /* 8. Build the Excel file (calls runQuoteFile from Phase 1) */
      const customerInfo = {
        hotel_name:   fields.hotel_name,
        contact_name: fields.contact_name,
        street:       fields.street,
        city:         fields.city,
        state:        fields.state,
        zip:          fields.zip,
        email:        fields.email,
        phone:        fields.phone,
        notes:        fields.notes
      };

      fileResult = await window.dfwXLSX.runQuoteFile(customerInfo, quoteId);

      /* 9. Convert to base64 */
      setStatus('Sending...', 'sending');
      base64Data = await blobToBase64(fileResult.file);

      /* 10. Fetch live cart for summary + items (best-effort, non-blocking) */
      let cartItemCount = 0;
      let cartSubtotalCents = 0;
      let cartItems = [];
      try {
        const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          cartItemCount = cart.item_count || 0;
          cartSubtotalCents = cart.total_price || 0;

          /* Build a slim items array for the staff email (first 50 sent over the wire).
             Keep field set small — Excel has full details. */
          if (Array.isArray(cart.items)) {
            cartItems = cart.items.slice(0, 50).map(function (item) {
              return {
                sku:        item.sku || '',
                title:      item.product_title || item.title || '',
                quantity:   item.quantity || 0,
                unit:       (item.properties && item.properties._uom) || 'Case',
                unit_price: (item.price || 0) / 100,
                line_total: (item.line_price || 0) / 100
              };
            });
          }
        }
      } catch (e) { /* ignore — Worker will work without these */ }

      /* 11. Build payload */
      const payload = {
        action:   WORKER_ACTION,
        quote_id: quoteId,
        turnstile_token: window._dfwQuoteToken || '',
        lead: {
          hotel_name:          customerInfo.hotel_name,
          contact_name:        customerInfo.contact_name,
          street:              customerInfo.street,
          city:                customerInfo.city,
          state:               customerInfo.state,
          zip:                 customerInfo.zip,
          email:               customerInfo.email,
          phone:               customerInfo.phone,
          notes:               customerInfo.notes,
          cart_item_count:     cartItemCount,
          cart_subtotal_cents: cartSubtotalCents,
          cart_items:          cartItems 
        },
        file: {
          name:         fileResult.filename,
          contentType:  fileResult.contentType,
          contentBytes: base64Data
        }
      };

      /* 12. POST to Worker */
      const response = await postToWorker(payload);

      /* 13. Success */
      console.log('[DFW Quote] Worker response:', response);

      if (WORKER_ACTION === 'check_config') {
        /* Phase 3 test mode — show test-friendly message */
        setStatus(
          'Test successful! See browser console for details. (' + quoteId + ')',
          'success'
        );
      } else {
        /* Phase 4 real send */
        setStatus(
          'Quote sent! Check your email for a copy. A customer service representative will contact you shortly.',
          'success'
        );
      }

      /* 14. Close modal after success (3 sec to let customer read message) */
      setTimeout(function () {
        closeModal();
        clearStatus();
        setSubmitting(false);
        form.reset();
        if (window.turnstile && window._dfwTsId !== undefined) window.turnstile.reset(window._dfwTsId);
        window._dfwQuoteToken = '';
      }, 3000);

    } catch (err) {
      console.error('[DFW Quote] Submit failed:', err);
      setStatus('Submission failed: ' + (err.message || 'Unknown error') + '. Please try again or contact us at (214) 745-8866.', 'error');
      if (window.turnstile && window._dfwTsId !== undefined) window.turnstile.reset(window._dfwTsId);
      window._dfwQuoteToken = '';
      setTimeout(function () { setSubmitting(false); }, 1500); }
    }
  /* ---------------------------------------------
     CLOSE MODAL HELPER
     --------------------------------------------- */
  function closeModal() {
    const overlay = qs(SELECTORS.overlay);
    const modal   = qs(SELECTORS.modal);
    if (overlay) overlay.classList.remove('is-open');
    if (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    }
  }

  /* ---------------------------------------------
     EVENT DELEGATION — intercept submits
     ---------------------------------------------
     We attach to document via capture phase so we
     intercept BEFORE dfw-quote.js's existing handler.
     Both files use the same selector, but ours runs
     first and either succeeds (Worker path) or
     bubbles to the other listener (form submit path
     for Phase 5 fallback).

     For Phase 3, we always handle the submit here
     and never bubble to the legacy handler.
     --------------------------------------------- */
  document.addEventListener('submit', function (e) {
    const form = e.target.closest(SELECTORS.form);
    if (!form) return;

    /* Stop both the native submit AND any other JS handlers */
    e.preventDefault();
    e.stopImmediatePropagation();

    handleSubmit(form);
  }, true);  /* capture = true */

  console.log('[DFW Quote Submit] Loaded. Action mode:', WORKER_ACTION);

})();

/* =========================================================
   Ankit END - DFW Quote Submit Orchestrator
   ========================================================= */